import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Overlay } from '~/ui/overlay/Overlay';

const RED = '#d81242';
const GREY_5 = '#2e2e2e';
const GREY_3 = '#a9a9a9';
const FONT = "'Plus Jakarta Sans', sans-serif";

const CenterWrap = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 420px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0px 15px 20px 0px rgba(0, 0, 0, 0.05),
    0px 30px 30px 0px rgba(0, 0, 0, 0.03),
    0px -2px 4px 0px rgba(0, 0, 0, 0.05);
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 16px 16px 12px 24px;
`;

const Header = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
`;

const TitleContent = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  flex: 1 0 0;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  letter-spacing: 0;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings:
    'lnum' 1,
    'pnum' 1;
  color: ${RED};
  word-break: break-word;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  width: 100%;
  padding: 16px 24px;
`;

const Message = styled.p`
  margin: 0;
  width: 100%;
  font-family: ${FONT};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings:
    'lnum' 1,
    'pnum' 1;
  color: ${GREY_5};
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;
  padding: 12px 24px 16px 24px;
`;

const Spacer = styled.div`
  flex: 1 0 0;
  height: 36px;
  min-width: 0;
`;

const CTAs = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 38px;
  padding: 8px 20px;
  border-radius: 8px;
  font-family: ${FONT};
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings:
    'lnum' 1,
    'pnum' 1;
  white-space: nowrap;
  cursor: pointer;

  ${props =>
    props.variant === 'primary'
      ? `
        background: ${RED};
        color: #ffffff;
        border: none;

        &:hover {
          background: #bf1039;
        }
      `
      : `
        background: transparent;
        color: ${GREY_5};
        border: 1px solid ${GREY_3};

        &:hover {
          background: #f5f5f5;
        }
      `}
`;

const AlertTriangleIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={RED}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={GREY_5}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
}

const ConfirmModalRed: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Delete',
  message = 'Are you sure you want to delete this item? This action cannot be undone.'
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return ReactDOM.createPortal(
    <Overlay open={isOpen} opacity={0.5} onClick={onClose}>
      <CenterWrap>
        <Card onClick={e => e.stopPropagation()}>
          <TitleSection>
            <Header>
              <AlertTriangleIcon />
              <TitleContent>
                <Title>{title}</Title>
              </TitleContent>
              <CloseButton
                aria-label="Close"
                onClick={e => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                <CloseIcon />
              </CloseButton>
            </Header>
          </TitleSection>

          <Body>
            <Message>{message}</Message>
          </Body>

          <Actions>
            <Spacer />
            <CTAs>
              <Button
                variant="secondary"
                onClick={e => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleConfirm();
                }}
              >
                Delete
              </Button>
            </CTAs>
          </Actions>
        </Card>
      </CenterWrap>
    </Overlay>,
    document.body
  );
};

export default ConfirmModalRed;
